/**
 * React Query hooks for API calls with caching
 */

import { useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import axios from 'axios';
import { queryKeys } from '../utils/queryClient';
import { WeatherReport } from '../types/weather';
import { ActivityDetails, ActivityPhoto } from '../types/chat';
import {
  WEATHER_URL,
  DESTINATION_INFO_URL,
  ACTIVITY_DETAILS_URL,
  ACTIVITY_PHOTOS_URL,
  STATES_URL,
} from '../constants/travel';
import { createLogger } from '../utils/logger';

const log = createLogger('useApiQueries');

type DestinationInfoQueryKey = ReturnType<typeof queryKeys.destination.info>;
type DestinationInfoQueryOptions = Omit<
  UseQueryOptions<any, Error, any, DestinationInfoQueryKey>,
  'queryKey' | 'queryFn'
>;

/**
 * Hook to fetch weather report
 */
export const useWeatherReport = (
  location: string | null | undefined,
  options?: UseQueryOptions<WeatherReport | null>
) => {
  return useQuery({
    queryKey: queryKeys.weather.byLocation(location || ''),
    queryFn: async (): Promise<WeatherReport | null> => {
      const trimmed = location?.trim();
      if (!trimmed) {
        return null;
      }

      const response = await axios.get<WeatherReport>(WEATHER_URL, {
        params: { location: trimmed },
      });

      const data = response.data as WeatherReport;

      return {
        resolvedLocation: data?.resolvedLocation ?? { query: trimmed },
        currentConditions: data?.currentConditions ?? null,
        dailyForecasts: Array.isArray(data?.dailyForecasts) ? data.dailyForecasts : [],
      };
    },
    enabled: !!location?.trim(),
    staleTime: 10 * 60 * 1000, // Weather updates every 10 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    ...options,
  });
};

/**
 * Hook to fetch destination info
 *
 * NOTE: Currently unused - destination info is streamed during trip generation.
 * This hook is maintained for the standalone /api/destination-info endpoint
 * which may be used for standalone destination queries in the future.
 */
export const useDestinationInfo = (
  destination: string | null | undefined,
  options?: DestinationInfoQueryOptions
) => {
  return useQuery({
    queryKey: queryKeys.destination.info(destination || ''),
    queryFn: async () => {
      if (!destination?.trim()) {
        return null;
      }

      const response = await axios.post(DESTINATION_INFO_URL, {
        destination: destination.trim(),
      });

      return response.data;
    },
    enabled: !!destination?.trim(),
    staleTime: 60 * 60 * 1000, // Destination info is relatively static - 1 hour
    gcTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours
    ...options,
  });
};

/**
 * Hook to fetch activity details
 */
export const useActivityDetails = (
  activityName: string | null | undefined,
  destinationName: string | null | undefined,
  locationName?: string,
  options?: UseQueryOptions<ActivityDetails>
) => {
  return useQuery({
    queryKey: queryKeys.activity.details(
      activityName || '',
      destinationName || '',
      locationName
    ),
    queryFn: async (): Promise<ActivityDetails> => {
      if (!activityName || !destinationName) {
        throw new Error('Activity name and destination name are required');
      }

      const response = await axios.post(ACTIVITY_DETAILS_URL, {
        activityName,
        destinationName,
        locationName: locationName || activityName,
      });

      return response.data as ActivityDetails;
    },
    enabled: !!activityName && !!destinationName,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    ...options,
  });
};

/**
 * Hook to fetch activity photos
 */
export const useActivityPhotos = (
  activityName: string | null | undefined,
  destinationName: string | null | undefined,
  activityId?: string | null,
  options?: UseQueryOptions<ActivityPhoto[]>
) => {
  return useQuery({
    queryKey: queryKeys.activity.photos(activityName || '', destinationName || ''),
    queryFn: async (): Promise<ActivityPhoto[]> => {
      if (!activityName || !destinationName) {
        return [];
      }

      try {
        const response = await axios.post(ACTIVITY_PHOTOS_URL, {
          activityName,
          destinationName,
          activityId: activityId || undefined,
        });

        // Handle new response format with single photo
        if (response.data.photo) {
          return [response.data.photo];
        }

        // Fallback for old format
        return response.data.photos || [];
      } catch (error: any) {
        log.error('Error getting activity photos', error);

        if (error.response?.status === 429) {
          log.warn('Rate limited while fetching activity photos');
          return [];
        }

        if (error.response?.status === 404) {
          log.warn('Photos endpoint unavailable, using placeholder');
          const searchQuery = `${activityName} ${destinationName}`.trim();
          return [
            {
              url: `https://via.placeholder.com/800x600/4285f4/ffffff?text=${encodeURIComponent(
                searchQuery
              )}`,
              width: 800,
              height: 600,
              attributions: ['Placeholder Image - Photo service temporarily unavailable'],
            },
          ];
        }

        throw new Error('Failed to get activity photos');
      }
    },
    enabled: !!activityName && !!destinationName,
    staleTime: 60 * 60 * 1000, // Photos don't change - 1 hour
    gcTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours
    retry: 1, // Only retry once for photos
    ...options,
  });
};

/**
 * Hook to fetch available states
 */
export const useAvailableStates = (options?: UseQueryOptions<string[]>) => {
  return useQuery({
    queryKey: queryKeys.states.available(),
    queryFn: async (): Promise<string[]> => {
      const response = await axios.get(STATES_URL);
      return response.data.states || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
};

/**
 * Hook to fetch a specific state by filename
 */
export const useStateByFilename = (
  filename: string | null | undefined,
  options?: UseQueryOptions<any>
) => {
  return useQuery({
    queryKey: queryKeys.states.byFilename(filename || ''),
    queryFn: async () => {
      if (!filename) {
        return null;
      }

      const response = await axios.post(`${STATES_URL}/load`, { filename });
      return response.data.state;
    },
    enabled: !!filename,
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 2 * 60 * 60 * 1000, // 2 hours
    ...options,
  });
};

/**
 * Prefetch hooks for optimistic loading
 */
export const usePrefetchWeather = () => {
  const queryClient = useQueryClient();

  return (location: string) => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.weather.byLocation(location),
      queryFn: async () => {
        const response = await axios.get<WeatherReport>(WEATHER_URL, {
          params: { location },
        });
        return response.data;
      },
    });
  };
};

/**
 * Hook to prefetch destination info.
 *
 * NOTE: Currently unused - destination info is streamed during trip generation.
 * This hook is maintained for potential future optimizations.
 */
export const usePrefetchDestination = () => {
  const queryClient = useQueryClient();

  return (destination: string) => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.destination.info(destination),
      queryFn: async () => {
        const response = await axios.post(DESTINATION_INFO_URL, {
          destination,
        });
        return response.data;
      },
    });
  };
};
